import {
  LOCALIZATION_CORRUPTION_RECOVERY_STARTED,
  LOCALIZATION_CORRUPTION_RECOVERY_ENQUEUE_STARTED,
  LOCALIZATION_CORRUPTION_RECOVERY_ENQUEUE_SUCCEEDED,
  LOCALIZATION_CORRUPTION_RECOVERY_PROGRESSED,
  LOCALIZATION_CORRUPTION_RECOVERY_FAILED,
  LOCALIZATION_CORRUPTION_RECOVERY_FINISHED,
  LOCALIZATION_CORRUPTION_REPORT_NODES_RESOLVED,
  LOCALIZATION_CORRUPTION_RECOVERY_ENABLED_REMOTELY,
  CORRUPTION_RECOVERY_PANEL_OPENED,
  CORRUPTION_RECOVERY_PANEL_CLOSED,
  LOST_TRANSLATIONS_FETCH_FINISHED,
  LOST_TRANSLATIONS_FETCH_FAILED,
  LOST_TRANSLATIONS_REPORT_STATUS_FETCH_FINISHED,
  LOST_TRANSLATIONS_REPORT_STATUS_FETCH_FAILED,
  LOST_TRANSLATIONS_REPORT_DOWNLOAD_REQUESTED,
  LOST_TRANSLATIONS_REPORT_DOWNLOAD_SUCCEEDED,
  LOST_TRANSLATIONS_REPORT_DOWNLOAD_FAILED,
  LOCALIZATION_CORRUPTION_REPORT_UPDATED_REMOTELY,
} from '@packages/systems/designer/actions';

import type {
  LocalizationIssuesPageAuditorResult,
  LocalizationIssuesComponentAuditorResult,
} from '@packages/shared/auditor-types';
import type {DesignerActionFromCreator} from '@packages/systems/designer/flux';
import type {
  CorruptionReport,
  LostTranslationRecord,
  LostTranslationsReportStatus,
  NodeCorruptionType,
} from '@packages/systems/localization-types';
import {createAction} from '@packages/utilities/create-action';

export const localizationCorruptionRecoveryStarted = createAction<
  typeof LOCALIZATION_CORRUPTION_RECOVERY_STARTED
>(LOCALIZATION_CORRUPTION_RECOVERY_STARTED);

export type LocalizationCorruptionRecoveryStartedAction =
  DesignerActionFromCreator<typeof localizationCorruptionRecoveryStarted>;

export const localizationCorruptionRecoveryEnqueueStarted = createAction<
  typeof LOCALIZATION_CORRUPTION_RECOVERY_ENQUEUE_STARTED
>(LOCALIZATION_CORRUPTION_RECOVERY_ENQUEUE_STARTED);

export type LocalizationCorruptionRecoveryEnqueueStartedAction =
  DesignerActionFromCreator<
    typeof localizationCorruptionRecoveryEnqueueStarted
  >;

export const localizationCorruptionRecoveryEnqueueSucceeded = createAction<
  typeof LOCALIZATION_CORRUPTION_RECOVERY_ENQUEUE_SUCCEEDED,
  {taskId: string}
>(LOCALIZATION_CORRUPTION_RECOVERY_ENQUEUE_SUCCEEDED);

export type LocalizationCorruptionRecoveryEnqueueSucceededAction =
  DesignerActionFromCreator<
    typeof localizationCorruptionRecoveryEnqueueSucceeded
  >;

export const localizationCorruptionRecoveryProgressed = createAction<
  typeof LOCALIZATION_CORRUPTION_RECOVERY_PROGRESSED,
  {progress: number}
>(LOCALIZATION_CORRUPTION_RECOVERY_PROGRESSED);

export type LocalizationCorruptionRecoveryProgressedAction =
  DesignerActionFromCreator<typeof localizationCorruptionRecoveryProgressed>;

export const localizationCorruptionRecoveryFailed = createAction<
  typeof LOCALIZATION_CORRUPTION_RECOVERY_FAILED
>(LOCALIZATION_CORRUPTION_RECOVERY_FAILED);

export type LocalizationCorruptionRecoveryFailedAction =
  DesignerActionFromCreator<typeof localizationCorruptionRecoveryFailed>;

export const localizationCorruptionRecoveryFinished = createAction<
  typeof LOCALIZATION_CORRUPTION_RECOVERY_FINISHED
>(LOCALIZATION_CORRUPTION_RECOVERY_FINISHED);

export type LocalizationCorruptionRecoveryFinishedAction =
  DesignerActionFromCreator<typeof localizationCorruptionRecoveryFinished>;

export const localizationCorruptionReportNodesResolved = createAction<
  typeof LOCALIZATION_CORRUPTION_REPORT_NODES_RESOLVED,
  {
    pageId?: string;
    branchId?: string;
    componentId?: string;
    localeId: string;
    nodeIds: string[];
  }
>(LOCALIZATION_CORRUPTION_REPORT_NODES_RESOLVED);

export type LocalizationCorruptionReportNodesResolvedAction =
  DesignerActionFromCreator<typeof localizationCorruptionReportNodesResolved>;

export const localizationCorruptionRecoveryEnabledRemotely = createAction<
  typeof LOCALIZATION_CORRUPTION_RECOVERY_ENABLED_REMOTELY
>(LOCALIZATION_CORRUPTION_RECOVERY_ENABLED_REMOTELY);

export type LocalizationCorruptionRecoveryEnabledRemotelyAction =
  DesignerActionFromCreator<
    typeof localizationCorruptionRecoveryEnabledRemotely
  >;

export const localizationCorruptionReportUpdatedRemotely = createAction<
  typeof LOCALIZATION_CORRUPTION_REPORT_UPDATED_REMOTELY,
  {report: CorruptionReport}
>(LOCALIZATION_CORRUPTION_REPORT_UPDATED_REMOTELY);

export type LocalizationCorruptionReportUpdatedRemotelyAction =
  DesignerActionFromCreator<typeof localizationCorruptionReportUpdatedRemotely>;

export const corruptionRecoveryPanelOpened = createAction<
  typeof CORRUPTION_RECOVERY_PANEL_OPENED,
  {
    auditResult:
      | LocalizationIssuesPageAuditorResult
      | LocalizationIssuesComponentAuditorResult;
  }
>(CORRUPTION_RECOVERY_PANEL_OPENED);

export type CorruptionRecoveryPanelOpenedAction = DesignerActionFromCreator<
  typeof corruptionRecoveryPanelOpened
>;

export const corruptionRecoveryPanelClosed = createAction<
  typeof CORRUPTION_RECOVERY_PANEL_CLOSED
>(CORRUPTION_RECOVERY_PANEL_CLOSED);

export type CorruptionRecoveryPanelClosedAction = DesignerActionFromCreator<
  typeof corruptionRecoveryPanelClosed
>;

export const lostTranslationsFetchFinished = createAction<
  typeof LOST_TRANSLATIONS_FETCH_FINISHED,
  {
    response: {
      records: LostTranslationRecord[];
      nodeId: string;
      corruptionType: NodeCorruptionType;
      localeId: string;
    };
  }
>(LOST_TRANSLATIONS_FETCH_FINISHED);

export type LostTranslationsFetchFinishedAction = DesignerActionFromCreator<
  typeof lostTranslationsFetchFinished
>;

export const lostTranslationsFetchFailed = createAction<
  typeof LOST_TRANSLATIONS_FETCH_FAILED
>(LOST_TRANSLATIONS_FETCH_FAILED);

export type LostTranslationsFetchFailedAction = DesignerActionFromCreator<
  typeof lostTranslationsFetchFailed
>;

export const lostTranslationsReportStatusFetchFinished = createAction<
  typeof LOST_TRANSLATIONS_REPORT_STATUS_FETCH_FINISHED,
  {response: LostTranslationsReportStatus}
>(LOST_TRANSLATIONS_REPORT_STATUS_FETCH_FINISHED);

export type LostTranslationsReportStatusFetchFinishedAction =
  DesignerActionFromCreator<typeof lostTranslationsReportStatusFetchFinished>;

export const lostTranslationsReportStatusFetchFailed = createAction<
  typeof LOST_TRANSLATIONS_REPORT_STATUS_FETCH_FAILED
>(LOST_TRANSLATIONS_REPORT_STATUS_FETCH_FAILED);

export type LostTranslationsReportStatusFetchFailedAction =
  DesignerActionFromCreator<typeof lostTranslationsReportStatusFetchFailed>;

export const lostTranslationsReportDownloadRequested = createAction<
  typeof LOST_TRANSLATIONS_REPORT_DOWNLOAD_REQUESTED
>(LOST_TRANSLATIONS_REPORT_DOWNLOAD_REQUESTED);

export type LostTranslationsReportDownloadRequestedAction =
  DesignerActionFromCreator<typeof lostTranslationsReportDownloadRequested>;

export const lostTranslationsReportDownloadFailed = createAction<
  typeof LOST_TRANSLATIONS_REPORT_DOWNLOAD_FAILED
>(LOST_TRANSLATIONS_REPORT_DOWNLOAD_FAILED);

export type LostTranslationsReportDownloadFailedAction =
  DesignerActionFromCreator<typeof lostTranslationsReportDownloadFailed>;

export {
  LOST_TRANSLATIONS_FETCH_FINISHED,
  LOST_TRANSLATIONS_FETCH_FAILED,
  LOST_TRANSLATIONS_REPORT_STATUS_FETCH_FINISHED,
  LOST_TRANSLATIONS_REPORT_STATUS_FETCH_FAILED,
  LOST_TRANSLATIONS_REPORT_DOWNLOAD_REQUESTED,
  LOST_TRANSLATIONS_REPORT_DOWNLOAD_SUCCEEDED,
  LOST_TRANSLATIONS_REPORT_DOWNLOAD_FAILED,
};
